import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/vercel/path0/packages/gatsby-theme-fuegodocs/src/components/Layout/Layout.js";
import data from "../../docs-data/Toast.docs.json";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Box = makeShortcode("Box");
const StyledToastExample = makeShortcode("StyledToastExample");
const ComponentProps = makeShortcode("ComponentProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`Toasts display brief, temporary notifications of actions, errors, or other events in an application.`}</p>
    <p>{`Toast adheres to the `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/wai-aria/#aria-live"
      }}>{`ARIA live requirements`}</a>{`.`}</p>
    <h2>{`Import`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { Toast } from "@fuegokit/react";
`}</code></pre>
    <h2>{`Usage`}</h2>
    <Box sx={{
      p: 4,
      border: "1px solid",
      borderRadius: 2,
      borderColor: "border.default"
    }} mdxType="Box">
  <StyledToastExample mdxType="StyledToastExample" />
    </Box>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Toast.Provider swipeDirection="right" duration={3000}>
  <Button
    onClick={() => {
      setOpen(false)
      window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => {
        eventDateRef.current = oneWeekAway()
        setOpen(true)
      }, 100)
    }}
  >
    Add to calendar
  </Button>

  <Toast open={open} onOpenChange={setOpen}>
    <ToastTitle>Scheduled: Catch up</ToastTitle>
    <ToastDescription asChild>
      <time dateTime={eventDateRef.current.toISOString()}>{prettyDate(eventDateRef.current)}</time>
    </ToastDescription>
    <ToastAction asChild altText="Goto schedule to undo">
      <Button
        spacing={'compact'}
        onClick={() => {
          setOpen(false)
        }}
      >
        Undo
      </AtlasButton>
    </ToastAction>
  </Toast>
  <ToastViewport />
</Toast.Provider>
`}</code></pre>
    <h2>{`Styling`}</h2>
    <p>{`To apply styles to the Toast primitive, use Fuegokit React's `}<inlineCode parentName="p">{`themeGet`}</inlineCode>{` function to access `}<a parentName="p" {...{
        "href": "https://fuegokit.design/tokens"
      }}>{`Fuegokit Tokens`}</a>{` directly.`}</p>
    <h3>{`Component API`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const StyledToastExample = () => {
  const [open, setOpen] = React.useState(false);

  return (
    <Toast.Provider duration={Infinity}>
      <AtlasButton
        onClick={() => {
          setOpen(!open);
        }}
      >
        Save
      </AtlasButton>

      {open && (
        <>
          <StyledToast open={open} onOpenChange={setOpen}>
            <StyledToastTitle></StyledToastTitle>
            <Box sx={{ mt: "-18px" }}>
              <StyledToastDescription asChild>
                <Text>Saved!</Text>
              </StyledToastDescription>
            </Box>
            <StyledToastClose onClick={() => setOpen(false)} aria-label="Close">
              <AkEditorCloseIcon aria-hidden />
            </StyledToastClose>
          </StyledToast>
          <StyledToastViewport />
        </>
      )}
    </Toast.Provider>
  );
};
`}</code></pre>
    <h3>{`CSS`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "path=/react/src/Toast/stories/StoryStyles.tsx src=https://github.com/fuegokit/fuegokit",
        "path": "/react/src/Toast/stories/StoryStyles.tsx",
        "src": "https://github.com/fuegokit/fuegokit"
      }}>{`const hide = keyframes\`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
\`;
const slideIn = keyframes\`
  from {
    transform: translateX(calc(100% + var(--viewport-padding)));
  }
  to {
    transform: translateX(0);
  }
\`;
const swipeOut = keyframes\`
  from {
    transform: translateX(var(--radix-toast-swipe-end-x));
  }
  to {
    transform: translateX(calc(100% + var(--viewport-padding)));
  }
\`;
const fadeIn = keyframes\`
  from {
    opacity: 0
  }
  to {
    opacity: 1
  }
\`;
const fadeOut = keyframes\`
  from {
    opacity: 1
  }
  to {
    opacity: 0
  }
\`;
const slideRight = keyframes\`
  from {
    transform: translateX(var(--radix-toast-swipe-end-x))
  }
  to {
    transform: translateX(calc(100% + \${VIEWPORT_PADDING}px))
  }
\`;
const slideLeft = keyframes\`
  from {
    transform: translateX(var(--radix-toast-swipe-end-x))
  }
  to {
    transform: translateX(calc(-100% - \${VIEWPORT_PADDING}px))
  }
\`;
const slideUp = keyframes\`
  from {
    transform: translateY(var(--radix-toast-swipe-end-y))
  }
  to {
    transform: translateY(calc(-100% - \${VIEWPORT_PADDING}px))
  }
\`;
const slideDown = keyframes\`
  from {
    transform: translateY(var(--radix-toast-swipe-end-y))
  }
  to {
    transform: translateY(calc(100% + \${VIEWPORT_PADDING}px))
  }
\`;
export const StyledToast = styled(Toast)\`
  background-color: \${themeGet("colors.elevation.surface.default.[default]")};
  border-radius: \${themeGet("radii.2")};
  box-shadow: \${themeGet("shadows.elevation.shadow.overlay")};
  padding: \${themeGet("space.4")};
  display: grid;
  grid-template-areas: "title action" "description action";
  grid-template-columns: auto max-content;
  column-gap: \${themeGet("space.4")};
  align-items: center;
  border: 1px solid \${themeGet("colors.border.subtle")};

  &[data-state="open"] {
    animation: \${slideIn} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state="closed"] {
    animation: hide 100ms ease-in;
  }
  &[data-swipe="move"] {
    transform: translateX(var(--radix-toast-swipe-move-x));
  }
  &[data-swipe="cancel"] {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }
  &[data-swipe="end"] {
    animation: \${swipeOut} 100ms ease-out;
  }
\`;
export const StyledToastViewport = styled(Toast.Viewport)\`
  --viewport-padding: \${themeGet("space.3")};
  position: fixed;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  padding: var(--viewport-padding);
  gap: \${themeGet("space.3")};
  width: 390px;
  max-width: 100vw;
  margin: 0;
  list-style: none;
  z-index: 2147483647;
  outline: none;
\`;
export const StyledToastTitle = styled(Toast.Title)\`
  grid-area: title;
  margin-bottom: \${themeGet("space.2")};
  font-weight: \${themeGet("fontWeights.semibold")};
  color: \${themeGet("colors.text.default")};
  font-size: \${themeGet("fontSizes.4")};
\`;
export const StyledToastDescription = styled(Toast.Description)\`
  grid-area: description;
  margin: 0;
  color: \${themeGet("colors.text.default")};
  font-size: \${themeGet("fontSizes.3")};
  line-height: \${themeGet("lineHeights.default")};
\`;
export const StyledToastAction = styled(Toast.Action)\`
  grid-area: action;
\`;
export const StyledToastClose = styled(Toast.Close)\`
  grid-area: action;
  // reset
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  user-select: none;
  display: flex;
  align-items: center;
  color: inherit;
  margin-left: \${themeGet("space.4")};
  font-weight: normal;
  font-size: \${themeGet("fontSizes.3")};
  text-decoration: none;
  opacity: 0.85;
  font-weight: \${themeGet("fontWeights.semibold")};
  padding: \${themeGet("space.2")} 12px;
  border-radius: \${themeGet("radii.1")};
  &:hover {
    opacity: 1;
    text-decoration: none;
    color: \${themeGet("colors.link.default")};
    background-color: \${themeGet("colors.background.neutral.subtle.hovered")};
  }

  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px \${themeGet("colors.border.focused")};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px \${themeGet("colors.border.focused")};
  }
  svg {
    transition: transform 0.15s ease;
  }
\`;
export const StyledToastHeader = styled(Box)\`
  padding: \${themeGet("space.3")} \${themeGet("space.4")};
  margin: -12px -12px 12px;
  background-color: \${themeGet("colors.background.neutral.bold.default")};
  color: \${themeGet("colors.text.inverse")};
  position: relative;
  height: \${themeGet("space.5")};
  display: flex;
  align-items: center;
\`;
`}</code></pre>
    <h2>{`Accessibility`}</h2>
    <p>{`Toast adheres to the `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/wai-aria/#aria-live"
      }}>{`ARIA live requirements`}</a>{`.`}</p>
    <h3>{`Sensitivity`}</h3>
    <p>{`Control the sensitivity of the toast for screen readers using the type prop.`}</p>
    <p>{`For toasts that are the result of a user action, choose `}<inlineCode parentName="p">{`foreground`}</inlineCode>{`. Toasts generated from background tasks should use `}<inlineCode parentName="p">{`background`}</inlineCode>{`.`}</p>
    <h3>{`Foreground`}</h3>
    <p>{`Foreground toasts are announced immediately.`}</p>
    <p>{`Assistive technologies may choose to clear previously queued messages when a foreground toast appears. Try to avoid stacking distinct `}<inlineCode parentName="p">{`foreground`}</inlineCode>{` toasts at the same time.`}</p>
    <h3>{`Background`}</h3>
    <p>{`Background toasts are announced at the next graceful opportunity, for example, when the screen reader has finished reading its current sentence.`}</p>
    <p>{`They do not clear queued messages so overusing them can be perceived as a laggy user experience for screen reader users when used in response to a user interaction.`}</p>
    <h2>{`Anatomy`}</h2>
    <p>{`A toast consists of a provider, an input which displays the current value, a list box popup, and an optional button used to toggle the list box popup open state.`}</p>
    <h2>{`Props`}</h2>
    <ComponentProps data={data} mdxType="ComponentProps" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      